import { IPricingData } from '@models/IPricingData';
import classNames from 'classnames';
import { AnimationProps, motion } from 'framer-motion';

interface Props {
  readonly centerText?: boolean;
  readonly fromPriceLabel?: string;
  readonly pricingData: IPricingData;
  readonly vatLabel?: string;
  readonly shippingLabel?: string;
  readonly rrpLabel?: string;
  readonly smallText?: boolean;
  readonly containerClassName?: string;
  readonly containerFontSize?: string;
  readonly taxesAnimationProps?: AnimationProps;
  readonly taxesTopMarginClassName?: string;
  readonly taxesClassName?: string;
  readonly taxesFontSize?: string;
  readonly priceClassName?: string;
  readonly dataTestId?: string;
  readonly isLightMode?: boolean;
}

const PriceDisplayText = ({
  fromPriceLabel,
  pricingData,
  vatLabel,
  shippingLabel,
  rrpLabel,
  centerText = false,
  smallText = false,
  containerClassName,
  containerFontSize = 'body-one',
  taxesAnimationProps = {},
  taxesTopMarginClassName = 'mt-2',
  taxesClassName,
  taxesFontSize = 'body-four',
  priceClassName,
  dataTestId,
  isLightMode,
}: Props) => {
  const {
    amountSaved,
    formattedPrice,
    formattedCalculatedPrice,
    shippingCost,
    price,
  } = pricingData;

  if (typeof price !== 'number') {
    return null;
  }

  return (
    <div
      className={classNames(
        containerClassName,
        smallText ? 'body-two font-light' : containerFontSize,
        {
          'flex flex-col items-center': centerText,
        }
      )}
      data-testid={dataTestId}
    >
      {((amountSaved > 0 && formattedPrice) || formattedCalculatedPrice) && (
        <span
          className={classNames('flex flex-wrap gap-x-2', {
            'justify-center': centerText,
          })}
        >
          {fromPriceLabel && <span>{fromPriceLabel}</span>}

          {amountSaved > 0 && formattedPrice && (
            <>
              {rrpLabel && <span className="mr-2">{rrpLabel}</span>}
              <span
                className="font-extralight line-through decoration-1"
                data-testid="Price-Display-Text-Discount-Price"
              >
                {formattedPrice}
              </span>
            </>
          )}

          {formattedCalculatedPrice && (
            <span
              className={classNames(
                {
                  'font-normal': !smallText,
                  'text-grey-450': amountSaved > 0,
                  'dark:text-yellow': amountSaved > 0 && !isLightMode,
                },
                priceClassName
              )}
              data-testid="Price-Display-Text-Price"
            >
              {formattedCalculatedPrice}
            </span>
          )}
        </span>
      )}

      {(vatLabel || (!shippingCost && shippingLabel)) && (
        <motion.p
          {...taxesAnimationProps}
          className={classNames(taxesClassName, taxesFontSize, 'block', {
            [`${taxesTopMarginClassName}`]: !smallText,
            'text-center': centerText,
          })}
          data-testid="Price-Display-Text-VAT/Shipping"
        >
          {vatLabel && vatLabel}
          {vatLabel && !shippingCost && shippingLabel && ' + '}
          {!shippingCost && shippingLabel && shippingLabel}
        </motion.p>
      )}
    </div>
  );
};

export default PriceDisplayText;
